import React from 'react';

export interface ContextWindowStatus {
  currentTokens: number;
  maxTokens: number;
  percentageUsed: number;
  remainingTokens: number;
  exceedsLimit: boolean;
  shouldWarn: boolean;
  messageCount: number;
}

export interface ContextWindowDisplayProps {
  status: ContextWindowStatus | null;
  onTruncate?: () => void;
  loading?: boolean;
}

export const ContextWindowDisplay: React.FC<ContextWindowDisplayProps> = ({
  status,
  onTruncate,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <div className="animate-pulse">Loading context status...</div>
        </div>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  const { currentTokens, maxTokens, percentageUsed, remainingTokens, exceedsLimit, shouldWarn } = status;

  // Determine color based on status
  const getStatusColor = () => {
    if (exceedsLimit) return 'text-red-600 dark:text-red-400';
    if (shouldWarn) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getProgressBarColor = () => {
    if (exceedsLimit) return 'bg-red-500';
    if (shouldWarn) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const percentage = Math.min(percentageUsed * 100, 100);

  return (
    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between gap-4">
        {/* Token count display */}
        <div className="flex items-center gap-2 text-sm">
          <span className={`font-medium ${getStatusColor()}`}>
            {currentTokens.toLocaleString()} / {maxTokens.toLocaleString()} tokens
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            ({percentage.toFixed(1)}% used)
          </span>
        </div>

        {/* Truncation button when exceeding limit */}
        {exceedsLimit && onTruncate && (
          <button
            onClick={onTruncate}
            className="px-3 py-1 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
          >
            Truncate Context
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${getProgressBarColor()}`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Warning message */}
      {shouldWarn && !exceedsLimit && (
        <div className="mt-2 text-xs text-yellow-600 dark:text-yellow-400">
          ⚠️ Context window is {percentage.toFixed(0)}% full. Consider starting a new conversation or truncating older messages.
        </div>
      )}

      {/* Exceeds limit message */}
      {exceedsLimit && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          ⚠️ Context window exceeded! The conversation has {(currentTokens - maxTokens).toLocaleString()} tokens over the limit. 
          Older messages may be truncated automatically, or you can manually truncate.
        </div>
      )}

      {/* Remaining tokens info */}
      {!exceedsLimit && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {remainingTokens.toLocaleString()} tokens remaining
        </div>
      )}
    </div>
  );
};
